/**
 * Volume detection for the TalentPop "Add more agents" button.
 *
 * The button surfaces when a team's support ticket volume is climbing —
 * specifically when month-to-date volume is at least 25% above the same
 * span of days in the previous month. This module is the pure half: the
 * date-window math and the surge comparison. The RPC that fetches the two
 * ticket counts lives in the trigger hook.
 */

/** A team's volume must reach this multiple of the prior period to fire. */
export const TALENTPOP_VOLUME_SURGE_RATIO = 1.25;

export interface DateWindow {
  /** Inclusive start of the window. */
  readonly start: Temporal.PlainDate;
  /** Inclusive end of the window. */
  readonly end: Temporal.PlainDate;
}

export interface VolumeComparisonWindows {
  /** This month, from the 1st through `today`. */
  readonly recent: DateWindow;
  /** Last month, from the 1st through the same day-of-month as `today`. */
  readonly prior: DateWindow;
}

/**
 * The two windows the volume trigger compares: month-to-date vs. the same
 * span of days in the previous month.
 *
 * When `today`'s day-of-month doesn't exist in the previous month (e.g. the
 * 31st), Temporal's `subtract` clamps to that month's last day, so the prior
 * window simply ends on the last valid day.
 */
export function getMonthToDateWindows(
  today: Temporal.PlainDate,
): VolumeComparisonWindows {
  const priorEnd = today.subtract({ months: 1 });
  return {
    recent: { start: today.with({ day: 1 }), end: today },
    prior: { start: priorEnd.with({ day: 1 }), end: priorEnd },
  };
}

/**
 * Whether `recentCount` is a surge over `priorCount` — at least a 25%
 * increase. Returns false when the prior period had no tickets: a team with
 * nothing to compare against isn't experiencing a "surge", and it avoids
 * dividing by zero.
 */
export function isVolumeSurge(
  recentCount: number,
  priorCount: number,
): boolean {
  if (priorCount <= 0) {
    return false;
  }
  return recentCount >= priorCount * TALENTPOP_VOLUME_SURGE_RATIO;
}

/** Once the volume trigger fires, the button stays visible this many days. */
export const TALENTPOP_VOLUME_PERSIST_DAYS = 1;

/**
 * Whether a recorded trigger time still keeps the button visible — true
 * within the 24-hour persistence window after `triggeredAt`. This smooths
 * the day-to-day flicker a live volume comparison would otherwise produce.
 * Returns false when the trigger has never fired (`triggeredAt` is null) or
 * the record is in the future.
 */
export function isWithinVolumePersistWindow(
  triggeredAt: Temporal.Instant | null,
  now: Temporal.Instant,
): boolean {
  if (triggeredAt === null) {
    return false;
  }
  const elapsedMs = now.epochMilliseconds - triggeredAt.epochMilliseconds;
  const windowMs = TALENTPOP_VOLUME_PERSIST_DAYS * 24 * 60 * 60 * 1000;
  return elapsedMs >= 0 && elapsedMs < windowMs;
}
